import { AuthUser } from '../types';

class AuthService {
  private currentUser: AuthUser | null = null;
  private listeners: ((user: AuthUser | null) => void)[] = [];

  constructor() {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage() {
    // Skip if running in a non-browser environment
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        this.currentUser = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading user from storage:', error);
      localStorage.removeItem('auth_user');
    }
  }

  private saveUserToStorage(user: AuthUser | null) {
    // Skip if running in a non-browser environment
    if (typeof window === 'undefined') {
      return;
    }
    
    try {
      if (user) {
        localStorage.setItem('auth_user', JSON.stringify(user));
      } else {
        localStorage.removeItem('auth_user');
      }
    } catch (error) {
      console.error('Error saving user to storage:', error);
    }
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentUser));
  }

  onAuthStateChange(callback: (user: AuthUser | null) => void) {
    this.listeners.push(callback);
    callback(this.currentUser); // Call immediately with current state
    
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  async signUp(email: string, password: string, username: string, displayName: string): Promise<AuthUser> {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check if username exists (simulate)
    const existingUsers = JSON.parse(localStorage.getItem('all_users') || '[]');
    if (existingUsers.some((u: any) => u.username === username)) {
      throw new Error('Username already exists');
    }

    if (existingUsers.some((u: any) => u.email === email)) {
      throw new Error('Email already exists');
    }

    const user: AuthUser = {
      id: `user-${Date.now()}`,
      email,
      username,
      displayName,
      createdAt: new Date()
    };

    // Save to users list
    existingUsers.push(user);
    localStorage.setItem('all_users', JSON.stringify(existingUsers));

    this.currentUser = user;
    this.saveUserToStorage(user);
    this.notifyListeners();

    return user;
  }

  async signIn(email: string, password: string): Promise<AuthUser> {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    const existingUsers = JSON.parse(localStorage.getItem('all_users') || '[]');
    const user = existingUsers.find((u: any) => u.email === email);

    if (!user) {
      throw new Error('User not found');
    }

    // In a real app, you'd verify the password here
    this.currentUser = user;
    this.saveUserToStorage(user);
    this.notifyListeners();

    return user;
  }

  async signOut(): Promise<void> {
    this.currentUser = null;
    this.saveUserToStorage(null);
    this.notifyListeners();
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }
}

export const auth = new AuthService();